/*
 * Delete into Trash and Restore from it (LEA-457), with Undo through the page's toast. LeadCue only:
 * the mail stays in Bison. No confirmation: Undo puts each back, and Trash keeps it seven days.
 */
import { appCount } from "../../src/app-dates.ts";
import type { InboxConversation } from "../../src/inbox-v2/contract.ts";
import { inboxApi } from "./api.ts";
import { useInboxWrite } from "./inbox-actions.ts";
import type { Toast } from "./use-inbox.ts";

const conversations = (n: number) => `${appCount(n)} conversation${n === 1 ? "" : "s"}`;

export function useTrashActions(workspaceId: string, onToast: (toast: Toast) => void) {
  const { write } = useInboxWrite(workspaceId, onToast);
  /** `restore`: the rows are listed in Trash, every one of them trashed. Resolves whether it was made. */
  const trash = async (rows: readonly InboxConversation[], restore: boolean) => {
    if (!rows.length) return false;
    const planIds = rows.map((row) => row.id);
    const apply = (ids: string[], value: boolean) => inboxApi.trash(workspaceId, ids, value);
    let previous: readonly { planId: string; value: boolean }[] = [];
    const words = { verb: restore ? "restore them" : "delete them", noun: "change" };
    const committed = await write("trash", planIds, words, async () => {
      previous = await apply(planIds, !restore);
    });
    if (!committed) return false;
    // Undo: each back to what it was, in at most two calls.
    const undo = async () => {
      for (const value of [true, false]) {
        const ids = previous.filter((row) => row.value === value).map((row) => row.planId);
        if (ids.length) await apply(ids, value);
      }
    };
    onToast({
      text: `${conversations(rows.length)} ${restore ? "restored" : "moved to Trash"}.`,
      undo: () => write("trash", planIds, { verb: "undo it", noun: "undo" }, undo),
    });
    return true;
  };
  return { trash };
}
